import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { money } from "@/dealership/lib/api";
import { computeTotals } from "@/dealership/lib/totals";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DealPayments from "@/dealership/components/DealPayments";
import { ArrowLeft, Pencil, Trash2, FileText, User, Car } from "lucide-react";

const STATUSES = ["Pending", "Deal Closed", "Paid", "Delivered"];
const statusStyle = {
  "Pending": "bg-zinc-500/12 text-zinc-400",
  "Deal Closed": "bg-amber-500/12 text-amber-400", "Paid": "bg-blue-500/12 text-blue-400",
  "Delivered": "bg-green-500/12 text-green-400",
};

function Row({ label, value, bold, accent }) {
  if (value === "" || value == null) return null;
  return (
    <div className={`flex justify-between py-2 border-b border-border last:border-0 text-sm ${bold ? "font-bold" : ""}`}>
      <span className="text-muted-foreground">{label}</span>
      <span className={accent ? "text-primary font-bold" : ""}>{value}</span>
    </div>
  );
}

export default function DealDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: deal, isLoading, isError } = useQuery({
    queryKey: ["deal", id],
    queryFn: async () => (await api.get(`/deals/${id}`)).data,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["deal", id] });
    qc.invalidateQueries({ queryKey: ["deals"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["deal-payment-audit", id] });
  };

  const statusMut = useMutation({
    mutationFn: async (status) => (await api.put(`/deals/${id}`, { ...deal, status })).data,
    onSuccess: () => { invalidate(); qc.invalidateQueries({ queryKey: ["inventory"] }); toast.success("Status updated"); },
    onError: () => toast.error("Failed to update status"),
  });
  const delMut = useMutation({
    mutationFn: async () => api.delete(`/deals/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["deals"] }); toast.success("Deal deleted"); navigate("/dealership/deals"); },
  });

  if (isLoading) return <div className="p-8 text-muted-foreground">Loading…</div>;
  if (isError || !deal) return <div className="p-8 text-muted-foreground">Deal not found. <button className="text-primary underline" onClick={() => navigate("/dealership/deals")}>Back to Deals</button></div>;

  const t = deal.totals || computeTotals(deal);
  const bal = deal.balance || {};
  const vehicle = deal.vehicle_desc || [deal.vehicle_year, deal.vehicle_make, deal.vehicle_model].filter(Boolean).join(" ");

  return (
    <div className="p-8 max-w-5xl" data-testid="deal-detail-page">
      <button onClick={() => navigate("/dealership/deals")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="deal-detail-back">
        <ArrowLeft className="h-4 w-4" /> Back to Deals
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-3xl">{deal.customer_name}</h1>
            <Badge className={`${statusStyle[deal.status] || "bg-zinc-500/12 text-zinc-400"} border-0`} data-testid="deal-detail-status-badge">{deal.status}</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1 font-mono-data">Bill #{deal.bill_number} · {vehicle || "No vehicle"}</p>
          {deal.status_changed_at && <p className="text-xs text-muted-foreground mt-1" data-testid="deal-detail-status-date">Status updated {(() => { try { return new Date(deal.status_changed_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return ""; } })()}</p>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={deal.status} onValueChange={(v) => statusMut.mutate(v)}>
            <SelectTrigger className="w-36" data-testid="deal-detail-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" onClick={() => navigate(`/dealership/bill/${id}`)} data-testid="deal-detail-bill"><FileText className="h-4 w-4 mr-2" />Bill of Sale</Button>
          <Button variant="outline" onClick={() => navigate(`/dealership/deals?edit=${id}`)} data-testid="deal-detail-edit"><Pencil className="h-4 w-4 mr-2" />Edit</Button>
          <Button variant="outline" className="text-destructive hover:text-destructive" onClick={() => { if (window.confirm("Delete this deal?")) delMut.mutate(); }} data-testid="deal-detail-delete"><Trash2 className="h-4 w-4 mr-2" />Delete</Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-3"><User className="h-4 w-4 text-muted-foreground" /><p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Customer</p></div>
          <Row label="Name" value={deal.customer_name} />
          <Row label="Phone" value={deal.customer_phone} />
          <Row label="Email" value={deal.customer_email} />
          <Row label="Address" value={[deal.customer_address, deal.customer_city, deal.customer_province, deal.customer_postal].filter(Boolean).join(", ")} />
          <Row label="License #" value={deal.customer_license} />
          <Row label="Salesperson" value={deal.salesperson} />
          <Row label="Deal Date" value={deal.deal_date} />
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-2 mb-3"><Car className="h-4 w-4 text-muted-foreground" /><p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Vehicle</p></div>
          <Row label="Description" value={vehicle} />
          <Row label="VIN" value={deal.vehicle_vin} />
          <Row label="Odometer" value={deal.vehicle_odometer ? `${Number(deal.vehicle_odometer).toLocaleString()} km` : ""} />
          <Row label="Trim / Color" value={[deal.vehicle_trim, deal.vehicle_color].filter(Boolean).join(" · ")} />
        </Card>

        <Card className="p-5 md:col-span-2">
          <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground mb-3">Financial Summary</p>
          <Row label="Sale Price" value={money(deal.sale_price)} />
          {deal.warranty_price ? <Row label={`Warranty${deal.warranty_name ? ` (${deal.warranty_name})` : ""}`} value={money(deal.warranty_price)} /> : null}
          {deal.admin_fee ? <Row label="Admin Fee" value={money(deal.admin_fee)} /> : null}
          {deal.safety_cert_fee ? <Row label="Safety / Cert Fee" value={money(deal.safety_cert_fee)} /> : null}
          {(deal.other_fees || []).map((f, i) => <Row key={i} label={f.label || "Fee"} value={money(f.amount)} />)}
          {deal.trade_in_value ? <Row label="Trade-In Allowance" value={`- ${money(deal.trade_in_value)}`} /> : null}
          <Row label="Taxable Net" value={money(t.taxable_net)} />
          <Row label={t.tax_exempt ? "HST" : "HST (13%)"} value={t.tax_exempt ? "Tax exempt" : money(t.hst)} />
          {t.non_taxable_fees ? <Row label="Non-Taxable Fees" value={money(t.non_taxable_fees)} /> : null}
          {deal.licensing_fee ? <Row label="Licensing (tax exempt)" value={money(deal.licensing_fee)} /> : null}
          {deal.lien_amount ? <Row label="Lien Payout" value={money(deal.lien_amount)} /> : null}
          <Row label="Total" value={money(t.total)} bold />
          {bal.total_paid ? <Row label="Total Paid" value={`- ${money(bal.total_paid)}`} /> : null}
          <Row label="Balance Due" value={bal.overpaid ? `Credit ${money(Math.abs(bal.balance_due))}` : (bal.paid_in_full ? "Paid in full" : money(bal.balance_due))} accent />
        </Card>
      </div>

      <div className="mt-6">
        <h2 className="font-heading text-xl mb-4">Payments</h2>
        <DealPayments deal={deal} onChanged={invalidate} />
      </div>
    </div>
  );
}
